/*
 * Bahar Art Creations — client-side predictive search engine.
 * Loads a static product index (assets/search-index.json), runs a normalized,
 * partial + typo-tolerant, attribute-aware match, and renders the dropdown
 * overlay and the full /search results page. Vanilla JS, no dependencies.
 *
 * Record shape (short keys to keep the index small):
 *   t title · u handle · i image url · p price (int $) · k "print"|"original"
 *   cat [category] (exactly one) · o orientation(portrait|landscape|square) · c [[label,hex]] · s [styles]
 */
(function () {
  'use strict';

  var CATEGORIES = ['metamorphosis', 'solitude', 'creatures', 'together-apart'];
  var CATEGORY_LABELS = { metamorphosis: 'Metamorphosis', solitude: 'Solitude', creatures: 'Creatures', 'together-apart': 'Together-Apart' };
  var ORIENTATIONS = ['portrait', 'landscape'];

  /* user word -> canonical token present in the data */
  var SYNONYMS = {
    vertical: 'portrait', tall: 'portrait', upright: 'portrait',
    horizontal: 'landscape', wide: 'landscape', 'wide-format': 'landscape',
    gray: 'grey', grey: 'grey',
    print: 'print', prints: 'print', framed: 'print',
    original: 'original', originals: 'original', painting: 'original'
  };

  var STOPWORDS = { the: 1, a: 1, an: 1, of: 1, and: 1, art: 1, artwork: 1, print: 0 };

  var state = { items: null, loading: null, byColor: null, byCategory: null, byOrient: null };

  /* ---------- normalization & matching ---------- */

  function norm(s) {
    return (s || '')
      .toString()
      .toLowerCase()
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .replace(/[^a-z0-9\s]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  function tokens(s) {
    return norm(s).split(' ').filter(function (t) { return t.length > 0; });
  }

  /* small, capped Levenshtein — returns true if distance <= max */
  function within(a, b, max) {
    if (a === b) return true;
    var al = a.length, bl = b.length;
    if (Math.abs(al - bl) > max) return false;
    var prev = [], cur = [], i, j;
    for (j = 0; j <= bl; j++) prev[j] = j;
    for (i = 1; i <= al; i++) {
      cur[0] = i;
      var best = cur[0];
      for (j = 1; j <= bl; j++) {
        var cost = a.charCodeAt(i - 1) === b.charCodeAt(j - 1) ? 0 : 1;
        cur[j] = Math.min(prev[j] + 1, cur[j - 1] + 1, prev[j - 1] + cost);
        if (cur[j] < best) best = cur[j];
      }
      if (best > max) return false;
      for (j = 0; j <= bl; j++) prev[j] = cur[j];
    }
    return prev[bl] <= max;
  }

  function canon(tok) { return SYNONYMS[tok] || tok; }

  /* score one product against one already-canonicalized query token */
  function scoreToken(item, tok) {
    var best = 0;
    var titleWords = item._t || (item._t = tokens(item.t));
    for (var i = 0; i < titleWords.length; i++) {
      var w = titleWords[i];
      if (w === tok) { best = Math.max(best, 100); }
      else if (w.indexOf(tok) === 0) { best = Math.max(best, 80); }
      else if (tok.length >= 3 && w.indexOf(tok) > 0) { best = Math.max(best, 55); }
      else if (tok.length >= 4 && within(w, tok, 1)) { best = Math.max(best, 45); }
    }
    /* full-title prefix (handles multi-word titles typed without spaces) */
    if (best < 80 && norm(item.t).replace(/ /g, '').indexOf(tok) === 0) best = Math.max(best, 70);

    if (item.o && (item.o === tok || item.o.indexOf(tok) === 0)) best = Math.max(best, 72);
    if (item.k === tok) best = Math.max(best, 40);

    var arrHit = function (arr, exact, partial) {
      if (!arr) return;
      for (var j = 0; j < arr.length; j++) {
        var v = arr[j];
        if (v === tok) best = Math.max(best, exact);
        else if (v.indexOf(tok) === 0) best = Math.max(best, partial);
        else if (tok.length >= 4 && within(v, tok, 1)) best = Math.max(best, partial - 20);
      }
    };
    arrHit(item._cat || (item._cat = (item.cat || []).map(norm)), 75, 60);
    arrHit(item._c || (item._c = (item.c || []).map(function (p) { return norm(p[0]); })), 74, 58);
    arrHit(item._s || (item._s = (item.s || []).map(norm)), 65, 48);
    return best;
  }

  function search(raw) {
    var items = state.items || [];
    var toks = tokens(raw).map(canon).filter(function (t) { return STOPWORDS[t] !== 1; });
    if (!toks.length) return { products: [], facets: [] };

    var out = [];
    for (var i = 0; i < items.length; i++) {
      var it = items[i], total = 0, ok = true;
      for (var k = 0; k < toks.length; k++) {
        var s = scoreToken(it, toks[k]);
        if (s === 0) { ok = false; break; }
        total += s;
      }
      if (ok) {
        if (it.k === 'print') total += 2; /* gentle: prints are the core catalog */
        out.push({ it: it, score: total });
      }
    }
    out.sort(function (a, b) { return b.score - a.score || a.it.t.localeCompare(b.it.t); });

    return { products: out.map(function (o) { return o.it; }), facets: facetsFor(toks) };
  }

  /* strict attribute filter — exact membership, NO text/title bleed.
     Used by the results page when a chip signals an attribute query (bsr=type). */
  function filterByAttribute(type, value) {
    var items = state.items || [];
    var v = canon(norm(value));
    var out = [];
    for (var i = 0; i < items.length; i++) {
      var it = items[i], ok = false;
      if (type === 'orientation') ok = it.o === v;
      else if (type === 'category') ok = (it._cat || (it._cat = (it.cat || []).map(norm))).indexOf(v) > -1;
      else if (type === 'color') ok = (it._c || (it._c = (it.c || []).map(function (p) { return norm(p[0]); }))).indexOf(v) > -1;
      else if (type === 'style') ok = (it._s || (it._s = (it.s || []).map(norm))).indexOf(v) > -1;
      if (ok) out.push(it);
    }
    out.sort(function (a, b) {
      var ap = a.k === 'print' ? 0 : 1, bp = b.k === 'print' ? 0 : 1;
      return ap - bp || a.t.localeCompare(b.t);
    });
    return out;
  }

  /* attribute chips whose label is prefix-matched by any query token */
  function facetsFor(toks) {
    ensureFacetIndex();
    var seen = {}, res = [];
    function consider(type, label, hex, map) {
      var key = type + ':' + label;
      if (seen[key]) return;
      for (var t = 0; t < toks.length; t++) {
        var tk = toks[t], nl = norm(label);
        if (nl === tk || nl.indexOf(tk) === 0 || (tk.length >= 4 && within(nl, tk, 1))) {
          seen[key] = 1;
          res.push({ type: type, label: label, hex: hex || '', count: (map[nl] || 0) });
          return;
        }
      }
    }
    Object.keys(state.byColor).forEach(function (nl) { var c = state.byColor[nl]; consider('color', c.label, c.hex, colorCounts()); });
    CATEGORIES.forEach(function (cat) { consider('category', cat, '', state.byCategory); });
    ORIENTATIONS.forEach(function (o) { consider('orientation', cap(o), '', state.byOrient); });
    res = res.filter(function (f) { return f.count > 0; });
    res.sort(function (a, b) { return b.count - a.count; });
    return res.slice(0, 6);
  }

  var _colorCounts;
  function colorCounts() {
    if (_colorCounts) return _colorCounts;
    _colorCounts = {};
    Object.keys(state.byColor).forEach(function (nl) { _colorCounts[nl] = state.byColor[nl].count; });
    return _colorCounts;
  }

  function ensureFacetIndex() {
    if (state.byColor) return;
    state.byColor = {}; state.byCategory = {}; state.byOrient = {};
    (state.items || []).forEach(function (it) {
      (it.c || []).forEach(function (pair) {
        var nl = norm(pair[0]);
        if (!nl) return;
        if (!state.byColor[nl]) state.byColor[nl] = { label: pair[0], hex: pair[1] || '', count: 0 };
        state.byColor[nl].count++;
      });
      (it.cat || []).forEach(function (cat) { var nl = norm(cat); state.byCategory[nl] = (state.byCategory[nl] || 0) + 1; });
      if (it.o) state.byOrient[it.o] = (state.byOrient[it.o] || 0) + 1;
    });
  }

  /* ---------- data loading ---------- */

  function load(url) {
    if (state.items) return Promise.resolve(state.items);
    if (state.loading) return state.loading;
    var cacheKey = 'bahar:search-index:' + url;
    try {
      var cached = sessionStorage.getItem(cacheKey);
      if (cached) { state.items = JSON.parse(cached).items; return Promise.resolve(state.items); }
    } catch (e) {}
    state.loading = fetch(url)
      .then(function (r) { return r.json(); })
      .then(function (data) {
        state.items = data.items || [];
        try { sessionStorage.setItem(cacheKey, JSON.stringify({ items: state.items })); } catch (e) {}
        return state.items;
      })
      .catch(function () { state.items = []; return state.items; });
    return state.loading;
  }

  /* ---------- recent searches ---------- */

  var RECENT_KEY = 'bahar:recent-searches';
  function recentsGet() {
    try { return JSON.parse(localStorage.getItem(RECENT_KEY)) || []; } catch (e) { return []; }
  }
  function recentsAdd(q) {
    q = (q || '').trim();
    if (q.length < 2) return;
    var list = recentsGet().filter(function (x) { return x.toLowerCase() !== q.toLowerCase(); });
    list.unshift(q);
    try { localStorage.setItem(RECENT_KEY, JSON.stringify(list.slice(0, 6))); } catch (e) {}
  }
  function recentsClear() { try { localStorage.removeItem(RECENT_KEY); } catch (e) {} }

  /* ---------- rendering ---------- */

  function esc(s) {
    return (s || '').toString().replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }
  function cap(s) { return s ? s.charAt(0).toUpperCase() + s.slice(1) : s; }

  function highlight(text, raw) {
    var safe = esc(text);
    var toks = tokens(raw).filter(function (t) { return t.length >= 2; });
    if (!toks.length) return safe;
    try {
      var re = new RegExp('(' + toks.map(function (t) { return t.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'); }).join('|') + ')', 'gi');
      return safe.replace(re, '<mark class="bsr-mark">$1</mark>');
    } catch (e) { return safe; }
  }

  function price(it) {
    var n = it.p || 0;
    var fmt = '$' + n.toLocaleString('en-US');
    return it.k === 'original' ? fmt : 'From ' + fmt;
  }

  function img(it, size) {
    if (!it.i) return '';
    var sep = it.i.indexOf('?') > -1 ? '&' : '?';
    return it.i + sep + 'width=' + (size || 120);
  }

  function chipUrl(f) {
    return '/search?q=' + encodeURIComponent(f.label) + '&type=product&bsr=' + f.type;
  }

  function facetChip(f) {
    var dot = f.type === 'color' && f.hex
      ? '<span class="bsr-chip__dot" style="background:' + esc(f.hex) + '"></span>'
      : '';
    var count = f.count ? '<span class="bsr-chip__count">' + f.count + '</span>' : '';
    var label = f.type === 'category' ? (CATEGORY_LABELS[f.label] || cap(f.label)) : cap(f.label);
    return '<a class="bsr-chip" data-nav href="' + chipUrl(f) + '">' + dot + esc(label) + count + '</a>';
  }

  function productRow(it, raw) {
    var image = it.i
      ? '<img src="' + esc(img(it, 104)) + '" alt="" class="bsr-row__img" width="52" height="52" loading="lazy">'
      : '<span class="bsr-row__img bsr-row__img--ph"></span>';
    var badge = it.k === 'original' ? '<span class="bsr-row__badge">Original</span>' : '';
    return '<a class="bsr-row" data-nav href="/products/' + esc(it.u) + '">'
      + image
      + '<span class="bsr-row__info">'
      + '<span class="bsr-row__title">' + highlight(it.t, raw) + badge + '</span>'
      + '<span class="bsr-row__price">' + esc(price(it)) + '</span>'
      + '</span></a>';
  }

  function sectionLabel(t) { return '<p class="bsr-label">' + esc(t) + '</p>'; }

  function renderResults(container, raw, opts) {
    opts = opts || {};
    var res = search(raw);
    var html = '';

    if (res.facets.length) {
      html += '<div class="bsr-group">' + sectionLabel('Browse by')
        + '<div class="bsr-chips">' + res.facets.map(facetChip).join('') + '</div></div>';
    }

    if (res.products.length) {
      var limit = opts.limit || 7;
      html += '<div class="bsr-group">' + sectionLabel('Artworks')
        + res.products.slice(0, limit).map(function (it) { return productRow(it, raw); }).join('')
        + '</div>';
      html += '<a class="bsr-viewall" data-nav href="/search?q=' + encodeURIComponent(raw) + '&type=product">'
        + 'View all results for &ldquo;' + esc(raw) + '&rdquo;'
        + '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5 12h14M13 6l6 6-6 6"/></svg></a>';
    }

    if (!res.facets.length && !res.products.length) {
      html = '<p class="bsr-empty">No matches for &ldquo;' + esc(raw) + '&rdquo;. Try a category, a colour, or portrait / landscape.</p>';
    }
    container.innerHTML = html;
  }

  function renderEmptyState(container) {
    ensureFacetIndex();
    var html = '';
    var recents = recentsGet();
    if (recents.length) {
      html += '<div class="bsr-group">' + sectionLabel('Recent')
        + '<div class="bsr-chips">'
        + recents.map(function (q) { return '<a class="bsr-chip" data-nav href="/search?q=' + encodeURIComponent(q) + '&type=product">' + esc(q) + '</a>'; }).join('')
        + '<button type="button" class="bsr-chip bsr-chip--ghost" data-clear-recent>Clear</button>'
        + '</div></div>';
    }
    html += '<div class="bsr-group">' + sectionLabel('Categories')
      + '<div class="bsr-chips">'
      + CATEGORIES.map(function (cat) { return '<a class="bsr-chip" data-nav href="/search?q=' + encodeURIComponent(cat) + '&type=product&bsr=category">' + esc(CATEGORY_LABELS[cat] || cap(cat)) + '</a>'; }).join('')
      + '</div></div>';
    html += '<div class="bsr-group">' + sectionLabel('Orientation')
      + '<div class="bsr-chips">'
      + ORIENTATIONS.map(function (o) { return '<a class="bsr-chip" data-nav href="/search?q=' + o + '&type=product&bsr=orientation">' + cap(o) + '</a>'; }).join('')
      + '</div></div>';

    var colors = Object.keys(state.byColor).map(function (nl) { return state.byColor[nl]; })
      .sort(function (a, b) { return b.count - a.count; }).slice(0, 12);
    if (colors.length) {
      html += '<div class="bsr-group">' + sectionLabel('Colour')
        + '<div class="bsr-chips">'
        + colors.map(function (c) {
            var dot = c.hex ? '<span class="bsr-chip__dot" style="background:' + esc(c.hex) + '"></span>' : '';
            return '<a class="bsr-chip" data-nav href="/search?q=' + encodeURIComponent(c.label) + '&type=product&bsr=color">' + dot + esc(c.label) + '</a>';
          }).join('')
        + '</div></div>';
    }
    return html;
  }

  /* ---------- keyboard navigation (roving focus) ---------- */

  function bindKeyboard(input, container) {
    input.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowDown') {
        var first = container.querySelector('[data-nav]');
        if (first) { e.preventDefault(); first.focus(); }
      }
    });
    container.addEventListener('keydown', function (e) {
      if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
      var navs = Array.prototype.slice.call(container.querySelectorAll('[data-nav]'));
      var idx = navs.indexOf(document.activeElement);
      e.preventDefault();
      if (e.key === 'ArrowDown') {
        if (idx < navs.length - 1) navs[idx + 1].focus();
      } else {
        if (idx > 0) navs[idx - 1].focus(); else input.focus();
      }
    });
  }

  /* ---------- public: overlay wiring ---------- */

  function initOverlay(cfg) {
    var input = cfg.input, container = cfg.results, url = cfg.indexUrl;
    if (!input || !container) return;
    var debounce, lastQuery = '';

    function showEmpty() { load(url).then(function () { container.innerHTML = renderEmptyState(container); }); }

    function run() {
      var q = input.value.trim();
      lastQuery = q;
      if (q.length < 1) { showEmpty(); return; }
      load(url).then(function () {
        if (input.value.trim() !== q) return;
        renderResults(container, q, { limit: 7 });
      });
    }

    input.addEventListener('input', function () { clearTimeout(debounce); debounce = setTimeout(run, 110); });
    input.addEventListener('focus', function () { if (!input.value.trim()) showEmpty(); });
    container.addEventListener('click', function (e) {
      if (e.target.closest('[data-clear-recent]')) { e.preventDefault(); recentsClear(); showEmpty(); return; }
      var nav = e.target.closest('[data-nav]');
      if (nav) recentsAdd(lastQuery);
    });
    if (cfg.form) cfg.form.addEventListener('submit', function () { recentsAdd(input.value); });
    bindKeyboard(input, container);

    /* warm the index on first open so first keystroke is instant */
    if (cfg.openTriggers) {
      cfg.openTriggers.forEach(function (b) { b.addEventListener('mouseenter', function () { load(url); }, { once: true }); });
    }
    load(url);
    cfg.onReady && cfg.onReady(showEmpty);
  }

  /* ---------- public: full results page augmentation ---------- */

  function initResultsPage(cfg) {
    var q = cfg.query, container = cfg.results, url = cfg.indexUrl;
    if (!q || !container) return;
    load(url).then(function () {
      var res = search(q);
      if (!res.products.length) return;
      var existing = {};
      Array.prototype.slice.call(container.querySelectorAll('[data-handle]')).forEach(function (n) { existing[n.getAttribute('data-handle')] = 1; });
      var extra = res.products.filter(function (it) { return !existing[it.u]; });
      if (cfg.onResults) cfg.onResults(res.products, extra, existing);
    });
  }

  window.BaharSearch = {
    load: load, search: search, filterByAttribute: filterByAttribute,
    initOverlay: initOverlay, initResultsPage: initResultsPage,
    renderResults: renderResults, recentsAdd: recentsAdd, highlight: highlight
  };

  /* self-init the header overlay from data attributes */
  function autoInit() {
    var input = document.querySelector('[data-search-input]');
    var results = document.getElementById('search-results');
    if (!input || !results) return;
    var url = input.getAttribute('data-search-index');
    if (!url) return;
    initOverlay({
      input: input,
      results: results,
      indexUrl: url,
      form: input.closest('form'),
      openTriggers: Array.prototype.slice.call(document.querySelectorAll('[data-open-search]'))
    });
  }
  if (document.readyState !== 'loading') autoInit();
  else document.addEventListener('DOMContentLoaded', autoInit);
})();
